import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type {
  Customer,
  CustomerCreateData,
  CustomerUpdateData,
  CustomerGroup,
  CustomerGroupCreateData,
  CustomerGroupUpdateData,
  CustomerListResponse,
  CustomerGroupListResponse,
  CustomerTerms,
  CustomerFilters,
  PricingTier,
  PricingTierCreateData,
  PricingTierUpdateData
} from '../../types/customer'
import type { RootState } from '../index'

export const customerApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Customer', 'CustomerGroup', 'PricingTier'],
  endpoints: (builder) => ({
    // Customer Management
    getCustomers: builder.query<CustomerListResponse, {
      page?: number
      per_page?: number
      customer_type?: string
      customer_group_id?: string
      is_active?: boolean
      search?: string
    }>({
      query: (params) => ({
        url: '/customers',
        params,
      }),
      providesTags: ['Customer'],
    }),
    getCustomer: builder.query<Customer, string>({
      query: (id) => `/customers/${id}`,
      providesTags: ['Customer'],
    }),
    createCustomer: builder.mutation<Customer, CustomerCreateData>({
      query: (data) => ({
        url: '/customers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Customer'],
    }),
    updateCustomer: builder.mutation<Customer, { id: string; data: CustomerUpdateData }>({
      query: ({ id, data }) => ({
        url: `/customers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Customer'],
    }),
    deleteCustomer: builder.mutation<void, string>({
      query: (id) => ({
        url: `/customers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Customer'],
    }),
    updateCustomerCreditLimit: builder.mutation<Customer, { id: string; creditLimit: number }>({
      query: ({ id, creditLimit }) => ({
        url: `/customers/${id}/credit-limit`,
        method: 'PATCH',
        body: { credit_limit: creditLimit },
      }),
      invalidatesTags: ['Customer'],
    }),
    assignCustomerToGroup: builder.mutation<Customer, { id: string; groupId: string }>({
      query: ({ id, groupId }) => ({
        url: `/customers/${id}/group`,
        method: 'PATCH',
        body: { customer_group_id: groupId },
      }),
      invalidatesTags: ['Customer'],
    }),
    getCustomerTerms: builder.query<CustomerTerms, string>({
      query: (id) => `/customers/${id}/terms`,
      providesTags: ['Customer'],
    }),
    assignCataloguesToCustomer: builder.mutation<Customer, { id: string; catalogueIds: string[] }>({
      query: ({ id, catalogueIds }) => ({
        url: `/customers/${id}/catalogues`,
        method: 'PATCH',
        body: { catalogue_ids: catalogueIds },
      }),
      invalidatesTags: ['Customer'],
    }),
    advancedCustomerSearch: builder.query<CustomerListResponse, CustomerFilters & {
      page?: number
      per_page?: number
    }>({
      query: (filters) => ({
        url: '/customers/search/advanced',
        params: filters,
      }),
      providesTags: ['Customer'],
    }),

    // Customer Group Management
    getCustomerGroups: builder.query<CustomerGroupListResponse, {
      page?: number
      per_page?: number
      is_active?: boolean
      search?: string
    }>({
      query: (params) => ({
        url: '/customers/groups',
        params,
      }),
      providesTags: ['CustomerGroup'],
    }),
    getCustomerGroup: builder.query<CustomerGroup, string>({
      query: (id) => `/customers/groups/${id}`,
      providesTags: ['CustomerGroup'],
    }),
    createCustomerGroup: builder.mutation<CustomerGroup, CustomerGroupCreateData>({
      query: (data) => ({
        url: '/customers/groups',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['CustomerGroup'],
    }),
    updateCustomerGroup: builder.mutation<CustomerGroup, { id: string; data: CustomerGroupUpdateData }>({
      query: ({ id, data }) => ({
        url: `/customers/groups/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CustomerGroup'],
    }),
    deleteCustomerGroup: builder.mutation<void, string>({
      query: (id) => ({
        url: `/customers/groups/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomerGroup'],
    }),
    applyGroupRules: builder.mutation<{ message: string; result: any }, string>({
      query: (groupId) => ({
        url: `/customers/groups/${groupId}/apply-rules`,
        method: 'POST',
      }),
      invalidatesTags: ['CustomerGroup', 'Customer'],
    }),

    // Pricing Tier Management
    getPricingTiers: builder.query<{ items: PricingTier[]; total: number; page: number; per_page: number; pages: number }, {
      page?: number
      per_page?: number
      is_active?: boolean
      search?: string
    }>({
      query: (params) => ({
        url: '/pricing/tiers',
        params,
      }),
      providesTags: ['PricingTier'],
    }),
    getPricingTier: builder.query<PricingTier, string>({
      query: (id) => `/pricing/tiers/${id}`,
      providesTags: ['PricingTier'],
    }),
    createPricingTier: builder.mutation<PricingTier, PricingTierCreateData>({
      query: (data) => ({
        url: '/pricing/tiers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PricingTier'],
    }),
    updatePricingTier: builder.mutation<PricingTier, { id: string; data: PricingTierUpdateData }>({
      query: ({ id, data }) => ({
        url: `/pricing/tiers/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['PricingTier'],
    }),
    deletePricingTier: builder.mutation<void, string>({
      query: (id) => ({
        url: `/pricing/tiers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PricingTier'],
    }),
    bulkUpdatePricing: builder.mutation<{ message: string; updated_count: number }, {
      tier_id?: string
      customer_ids?: string[]
      price_adjustments: Record<string, number>
    }>({
      query: (data) => ({
        url: '/pricing/bulk-update',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Customer', 'PricingTier'],
    }),

    // Bulk Operations
    bulkExportCustomers: builder.mutation<Blob, {
      customer_ids: string[]
      format: 'csv' | 'excel'
    }>({
      query: (data) => ({
        url: '/customers/bulk-export',
        method: 'POST',
        body: data,
        responseHandler: async (response) => {
          return await response.blob()
        },
      }),
    }),
    bulkUpdateCustomerStatus: builder.mutation<{
      total: number
      successful: Array<{
        customer_id?: string
        name?: string
        new_status?: string
        error?: string
      }>
      failed: Array<{
        customer_id?: string
        name?: string
        new_status?: string
        error?: string
      }>
    }, {
      customer_ids: string[]
      is_active: boolean
    }>({
      query: (data) => ({
        url: '/customers/bulk-status-update',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Customer'],
    }),
  }),
})

export const {
  // Customer hooks
  useGetCustomersQuery,
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useUpdateCustomerCreditLimitMutation,
  useAssignCustomerToGroupMutation,
  useGetCustomerTermsQuery,
  useAssignCataloguesToCustomerMutation,
  useAdvancedCustomerSearchQuery,

  // Customer Group hooks
  useGetCustomerGroupsQuery,
  useGetCustomerGroupQuery,
  useCreateCustomerGroupMutation,
  useUpdateCustomerGroupMutation,
  useDeleteCustomerGroupMutation,
  useApplyGroupRulesMutation,

  // Pricing Tier hooks
  useGetPricingTiersQuery,
  useGetPricingTierQuery,
  useCreatePricingTierMutation,
  useUpdatePricingTierMutation,
  useDeletePricingTierMutation,
  useBulkUpdatePricingMutation,

  // Bulk Operations hooks
  useBulkExportCustomersMutation,
  useBulkUpdateCustomerStatusMutation,
} = customerApi